import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { LoaderCircle } from 'lucide-react';
import { cn } from '../lib/utils.js';

export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-(--control-height) px-4',
        sm: 'h-(--control-height-sm) px-3 text-[13px]',
        lg: 'h-11 px-6 text-base',
        icon: 'size-(--control-height)',
        'icon-sm': 'size-(--control-height-sm)',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

/**
 * @param {import('react').ComponentProps<'button'> & {
 *   variant?: 'default'|'destructive'|'outline'|'secondary'|'ghost'|'link',
 *   size?: 'default'|'sm'|'lg'|'icon'|'icon-sm',
 *   asChild?: boolean,
 *   loading?: boolean,
 * }} props
 */
export function Button({ className, variant, size, asChild = false, loading = false, disabled, children, type, ...props }) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant ?? 'default'}
      data-size={size ?? 'default'}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      type={asChild ? undefined : (type ?? 'button')}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <LoaderCircle className="animate-spin" aria-hidden="true" />}
          {children}
        </>
      )}
    </Comp>
  );
}
